import { useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { BasicInfoStep } from "../BasicInfoStep";
import { FinancialInfoStep } from "../FinancialInfoStep";
import { StrategicAlignmentStep } from "../StrategicAlignmentStep";
import { ContactsStep } from "../ContactsStep";
import { ChevronLeft, ChevronRight, FileText, Check, X, Home } from "lucide-react";
import { Badge } from "../ui/badge";

export interface DemandFormData {
  // Basic Info
  demandTitle: string;
  description: string;
  priority: string;
  department: string;
  requestedBy: string;
  businessCase: string;
  
  // Financial Info
  estimatedCost: string;
  budgetCategory: string;
  costJustification: string;
  expectedROI: string;
  paybackPeriod: string;
  
  // Strategic Alignment
  strategicObjective: string;
  businessImpact: string;
  riskAssessment: string;
  alternatives: string;
  
  // Contacts
  primaryContact: string;
  primaryEmail: string;
  primaryPhone: string;
  secondaryContact: string;
  secondaryEmail: string;
  approver: string;
  approverEmail: string;
  
  // Documents
  supportingDocuments: FileList | null;
}

const steps = [
  {
    id: 1,
    title: "Basic Information",
    description: "Essential details about your demand",
    icon: FileText
  },
  {
    id: 2,
    title: "Financial Information",
    description: "Budget and cost details",
    icon: FileText
  },
  {
    id: 3,
    title: "Strategic Alignment",
    description: "Business objectives and impact",
    icon: FileText
  },
  {
    id: 4,
    title: "Contacts",
    description: "Key stakeholders and approvers",
    icon: FileText
  }
];

interface DemandIntakeFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

export function DemandIntakeForm({ onCancel, onSuccess }: DemandIntakeFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<DemandFormData>({
    mode: "onChange",
    defaultValues: {
      demandTitle: "",
      description: "",
      priority: "",
      department: "",
      requestedBy: "",
      businessCase: "",
      estimatedCost: "",
      budgetCategory: "",
      costJustification: "",
      expectedROI: "",
      paybackPeriod: "",
      strategicObjective: "",
      businessImpact: "",
      riskAssessment: "",
      alternatives: "",
      primaryContact: "",
      primaryEmail: "",
      primaryPhone: "",
      secondaryContact: "",
      secondaryEmail: "",
      approver: "",
      approverEmail: "",
      supportingDocuments: null
    }
  });

  const { watch, trigger, getValues } = form;
  const watchedValues = watch();

  // Calculate progress based on filled fields
  const calculateProgress = () => {
    const allFields = Object.keys(form.getValues()) as (keyof DemandFormData)[];
    const filledFields = allFields.filter(field => {
      const value = watchedValues[field];
      if (field === 'supportingDocuments') {
        return value && value.length > 0;
      }
      return value && value.toString().trim() !== '';
    });
    return Math.round((filledFields.length / allFields.length) * 100);
  };

  const progress = calculateProgress();

  // Validate current step
  const validateCurrentStep = async () => {
    let fieldsToValidate: (keyof DemandFormData)[] = [];
    
    switch (currentStep) {
      case 1:
        fieldsToValidate = ['demandTitle', 'description', 'priority', 'department', 'requestedBy', 'businessCase'];
        break;
      case 2:
        fieldsToValidate = ['estimatedCost', 'budgetCategory', 'costJustification', 'expectedROI', 'paybackPeriod'];
        break;
      case 3:
        fieldsToValidate = ['strategicObjective', 'businessImpact', 'riskAssessment', 'alternatives'];
        break;
      case 4:
        fieldsToValidate = ['primaryContact', 'primaryEmail', 'approver', 'approverEmail'];
        break;
    }
    
    const isValid = await trigger(fieldsToValidate);
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep || completedSteps.includes(step - 1)) {
      setCurrentStep(step);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? All unsaved changes will be lost.")) {
      onCancel();
    }
  };

  const onSubmit = async (data: DemandFormData) => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log("Form submitted:", data);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      } else {
        // Fallback to alert if no success callback
        alert("Demand submitted successfully!");
        onCancel();
      }
    } catch (error) {
      console.error("Submission failed:", error);
      alert("Failed to submit demand. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfoStep form={form} />;
      case 2:
        return <FinancialInfoStep form={form} />;
      case 3:
        return <StrategicAlignmentStep form={form} />;
      case 4:
        return <ContactsStep form={form} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header with Cancel Button */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl mb-2">Submit New Demand</h1>
            <p className="text-muted-foreground">
              Complete all sections to submit your demand for review and approval.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Home className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex items-center gap-2"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Overall Progress</span>
                <span className="text-sm">{progress}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Step Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {steps.map((step) => {
            const isActive = currentStep === step.id;
            const isCompleted = completedSteps.includes(step.id);
            const isAccessible = step.id <= currentStep || completedSteps.includes(step.id - 1);
            
            return (
              <Card
                key={step.id}
                className={`cursor-pointer transition-all ${
                  isActive 
                    ? "ring-2 ring-primary border-primary" 
                    : isCompleted 
                    ? "border-green-500" 
                    : isAccessible 
                    ? "hover:border-primary/50" 
                    : "opacity-50 cursor-not-allowed"
                }`}
                onClick={() => isAccessible && !isSubmitting && handleStepClick(step.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted 
                        ? "bg-green-500 text-white" 
                        : isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <span className="text-sm">{step.id}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{step.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Form */}
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge variant="outline">Step {currentStep} of 4</Badge>
                {steps[currentStep - 1].title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1 || isSubmitting}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="flex items-center gap-3">
              {currentStep < 4 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Submit Demand
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}